import lodash from "lodash";
import { useEffect } from "react";
import { Socket, io } from "socket.io-client";
import urls from "../global/constants/UrlConstants";
import {
  isTruthy,
  openErrorNotification,
  updateSessionItemsAndWaiterAddedBasedOnGuids,
  compareStrings,
} from "../helpers/methods";
import {
  addFlagforSocket,
  updateIsLocationOnline,
  updateIsUTTLocked,
} from "../redux/generalSlice";
import {
  addSession,
  updateSplittableState,
  updateCanPlaceOrder,
} from "../redux/menuSlice";
import {
  PayPageSessionItems,
  OrderedItem,
  SplitPayUpdate,
  GUID,
} from "../socketType";
import { store } from "./store";
import history from "./history";
import { globalEmitter } from "./emitter";
import strings from "../global/constants/StringConstants";
import { checkForPaymentMethod } from "../screens/Cart/CartService";

interface CustomProps {
  sessionId: string;
  restaurantId: string;
  tableNumber: string;
}

const Sockets = (props: CustomProps) => {
  useEffect(() => {
    if (
      isTruthy(props.restaurantId) &&
      isTruthy(props.sessionId) &&
      isTruthy(props.tableNumber)
    ) {
      // Socket Connection Init
      const socket = io(`${urls.socket_endpoint}dinein/${props.restaurantId}`, {
        query: {
          sessionId: props.sessionId,
          table: props.tableNumber.toString(),
        },
        withCredentials: true,
      });

      socket.on("connect", () => {
        store.dispatch(addFlagforSocket(true));
      });

      socket.on("connect_error", (err: any) => {
        let prevIsSocketConnection =
          store.getState().general.isSocketConnection;
        if (prevIsSocketConnection || prevIsSocketConnection === null) {
          openErrorNotification("Network Issue. Please check your connection.");
        }
        store.dispatch(addFlagforSocket(false));
      });

      socket.on("disconnect", () => {
        // console.log("Socket disconnected", AppSockets.socket.id);
      });

      socket.on("items-added", (addedItems: PayPageSessionItems) => {
        let currentSession = lodash.cloneDeep(store.getState().menu.session);
        if (currentSession.hasOwnProperty("sessionItems")) {
          Object.keys(addedItems).forEach((sessionId: string) => {
            if (currentSession.sessionItems.hasOwnProperty(sessionId)) {
              currentSession.sessionItems[sessionId].orders = {
                ...currentSession.sessionItems[sessionId].orders,
                ...addedItems[sessionId].orders,
              };
            } else {
              currentSession.sessionItems[sessionId] = addedItems[sessionId];
            }
          });
        } else {
          currentSession.sessionItems = addedItems;
        }
        store.dispatch(addSession(currentSession));
      });

      socket.on("waiter-items-added", (addedItems: Partial<OrderedItem>[]) => {
        let currentSession = lodash.cloneDeep(store.getState().menu.session);
        currentSession.waiterAdded = addedItems;
        store.dispatch(addSession(currentSession));
      });

      socket.on("pay-items-updated", (state: SplitPayUpdate) => {
        let currentSession = lodash.cloneDeep(store.getState().menu.session);
        currentSession.splitPayState = state;
        store.dispatch(addSession(currentSession));
      });

      socket.on("items-paid", (state: GUID[]) => {
        updateSessionItemsAndWaiterAddedBasedOnGuids(state, false);
      });

      socket.on("items-refunded", (state: GUID[]) => {
        updateSessionItemsAndWaiterAddedBasedOnGuids(state, true);
      });

      socket.on("ticket-closed", (state: GUID[]) => {
        let currentSession = lodash.cloneDeep(store.getState().menu.session);
        const sessionItems = currentSession.sessionItems;
        const waiterAddedItems = currentSession.waiterAdded;
        Object.keys(sessionItems).forEach((sessionId: string) => {
          Object.keys(sessionItems[sessionId].orders).forEach(
            (orderId: string) => {
              const orders = sessionItems[sessionId].orders[orderId].filter(
                (item) => item.guid && !state.includes(item.guid)
              );
              return orders ?? [];
            }
          );
        });
        const updatedWaiterAddedItems = waiterAddedItems.filter(
          (item) => item.guid && !state.includes(item.guid)
        );
        currentSession.waiterAdded = updatedWaiterAddedItems;
        currentSession.sessionItems = sessionItems;
        store.dispatch(addSession(currentSession));
      });

      socket.on("items-splittable", (state: boolean) => {
        store.dispatch(updateSplittableState(state));
        const splittableReduxState = store
          .getState()
          .menu.splittable.toString();
        if (compareStrings(splittableReduxState, state.toString())) {
          return;
        }
        if (window.location.pathname.includes(urls.paymentViewPath)) {
          history.push(urls.menuViewPath);
        }
      });

      socket.on(
        "pay-all-items-updated",
        async (state: {
          parsedSplitPayState: SplitPayUpdate;
          sessionId: string;
        }) => {
          const orderingEnabled = store.getState().general.orderingEnabled;
          const sessionId = store.getState().menu.sessionId;
          let currentSession = lodash.cloneDeep(store.getState().menu.session);
          currentSession.splitPayState = state.parsedSplitPayState;
          store.dispatch(addSession(currentSession));
          // Only redirects the pay-all emit sender use
          if (sessionId === state.sessionId) {
            if (
              !orderingEnabled &&
              (window.location.pathname.includes(urls.menuViewPath) ||
                window.location.pathname.includes(urls.paymentViewPath))
            ) {
              history.push(urls.paymentViewPath);
            } else {
              // Ordering with SplitNPay Flow
              const response = await checkForPaymentMethod();
              if (response.selected) {
                history.push(urls.paymentViewPath);
              } else {
                history.push({
                  pathname: urls.paymentMethodViewPath,
                  state: { fromURL: urls.menuViewPath, fromPage: strings.PAY },
                });
              }
            }
          }
        }
      );

      socket.on("items-vanished", (state: boolean) => {
        if (window.location.pathname.includes(urls.paymentViewPath)) {
          history.push(urls.menuViewPath);
        }
      });

      socket.on("location-online", (state: boolean) => {
        store.dispatch(updateIsLocationOnline(state));
        if (!state) {
          if (
            window.location.pathname.includes(urls.paymentViewPath) ||
            window.location.pathname.includes(urls.paymentMethodViewPath)
          ) {
            history.push(urls.menuViewPath);
          }
        }
      });

      socket.on("can-place-order", (state: boolean) => {
        store.dispatch(updateCanPlaceOrder(state));
      });

      // socket.on("is-utt-locked", (state: boolean) => {
      //   store.dispatch(updateIsUTTLocked(state));
      // });

      globalEmitter.addListener(strings.socketEmit, (event) =>
        handleSocket(event, socket)
      );

      return () => {
        socket.close();
        globalEmitter.removeListener(strings.socketEmit, (event) =>
          handleSocket(event, socket)
        );
      };
    }
  }, [props.sessionId, props.restaurantId, props.tableNumber]);

  const handleSocket = (
    event: { event: string; params: any },
    socket: Socket
  ) => {
    switch (event.event) {
      case "emitPayFor": {
        socket?.emit("pay-for", { items: event.params });
        break;
      }
      case "emitSplitPayUpdateForAllItemSelection": {
        socket?.emit("pay-all", { items: event.params });
        break;
      }
      case "emitMultiRemovePayFor": {
        // multiple items are removed based on this socket
        socket?.emit("multi-remove-pay-for");
        break;
      }
      case "emitRemovePayFor": {
        socket?.emit("remove-pay-for", { items: event.params });
        break;
      }
      case "emitUnlockItems": {
        // items are unlocked based on this sockets sessionId on backend
        socket?.emit("unlock-items");
        break;
      }
      case "checkSocketConnection": {
        return socket?.connected ?? false;
      }
    }
  };

  return null;
};

export default Sockets;
